import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const mixedOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        propertiesHousesRangesAreaMin: props.region.stats?.properties_houses_ranges_area_min || 0,
        propertiesHousesRangesAreaMax: props.region.stats?.properties_houses_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        housePriceMin: props.region.stats?.properties_houses_ranges_price_min || 0,
        housePriceMax: props.region.stats?.properties_houses_ranges_price_max || 0,
        housesOffersCountForSale: props.region.stats?.offers_houses_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0
    };

    const getNonZeroMinValue = (...values: number[]) => {
        return Math.min(...values.filter(Number));
    };

    const getMaxValue = (...values: number[]) => {
        return Math.max(...values);
    };

    const offerCountForSale = variables.housesOffersCountForSale + variables.flatsOffersCountForSale;
    const propertiesCountForSale = variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale;
    const propertiesRangesAreaMin = getNonZeroMinValue(variables.propertiesFlatsRangesAreaMin, variables.propertiesHousesRangesAreaMin);
    const propertiesRangesAreaMax = getMaxValue(variables.propertiesFlatsRangesAreaMax, variables.propertiesHousesRangesAreaMax);
    const propertyPriceMin = getNonZeroMinValue(variables.flatPriceMin, variables.housePriceMin);
    const currency = props.region.country === Country.SPAIN ? "euro" : "zł";

    if (!offerCountForSale || !propertiesCountForSale) {
        return [];
    }

    return [
        {
            headline: `Nieruchomości na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.housesOffersCountForSale + variables.flatsOffersCountForSale,
                    propertiesCountForSale: variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale,
                    offerTypeName: "nowych nieruchomości"
                }),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Deweloperzy ${variables.regionNameDeclensionWhere} mają w sprzedaży nieruchomości `,
                        `${pluralize([
                            `o powierzchni ${areaFormat(propertiesRangesAreaMin)}`,
                            `o powierzchniach od ${propertiesRangesAreaMin}${
                                propertiesRangesAreaMax !== propertiesRangesAreaMin ? ` do ${areaFormat(propertiesRangesAreaMax)}` : ` m2.`
                            }`,
                            `o powierzchniach od ${propertiesRangesAreaMin}${propertiesRangesAreaMax !== propertiesRangesAreaMin ? ` do ${areaFormat(propertiesRangesAreaMax)}` : " m2."}`
                        ])(variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale)}`
                    ]
                }
            ]
        },
        {
            headline: `Ile kosztują nieruchomości ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańsza nieruchomość ${variables.regionNameDeclensionWhere} kosztuje ${priceFormat(propertyPriceMin, {unit: currency})}.`,
                        variables.averagePriceM2 > 0
                            ? ` Średnia cena za m2 nieruchomości ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2, {unit: currency})}.`
                            : ""
                    ]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach nieruchomości ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na nieruchomości.`
                      },
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Chcesz mieszkać w domu z własnym ogródkiem? Poznaj `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href:
                                      props.region.country === Country.SPAIN
                                          ? rpAppLink.offer.listFriendlyAbroad({
                                                friendlySlug: `nowe-domy`,
                                                country: `hiszpania`,
                                                abroadRegionSlug: `${variables.regionSlug}`
                                            })
                                          : rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `ofertę nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych domów ${variables.regionNameDeclensionWhere}`,
                        `. Wolisz mieszkanie w mieście? Sprawdź `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href:
                                      props.region.country === Country.SPAIN
                                          ? rpAppLink.offer.listFriendlyAbroad({
                                                friendlySlug: `nowe-mieszkania`,
                                                country: `hiszpania`,
                                                abroadRegionSlug: `${variables.regionSlug}`
                                            })
                                          : rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `nowe mieszkania ${variables.regionNameDeclensionWhere}`
                              }
                            : `nowe mieszkania ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }
    ];
};
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType, TextType} from "./constants";

export const mixedOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        propertiesHousesRangesAreaMin: props.region.stats?.properties_houses_ranges_area_min || 0,
        propertiesHousesRangesAreaMax: props.region.stats?.properties_houses_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        housePriceMin: props.region.stats?.properties_houses_ranges_price_min || 0,
        housePriceMax: props.region.stats?.properties_houses_ranges_price_max || 0,
        housesOffersCountForSale: props.region.stats?.offers_houses_count_for_sale || 0,
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0
    };

    const getNonZeroMinValue = (...values: number[]) => {
        return Math.min(...values.filter(Number));
    };

    const getMaxValue = (...values: number[]) => {
        return Math.max(...values);
    };

    const offerCountForSale = variables.housesOffersCountForSale + variables.flatsOffersCountForSale;
    const propertiesCountForSale = variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale;
    const propertiesRangesAreaMin = getNonZeroMinValue(variables.propertiesFlatsRangesAreaMin, variables.propertiesHousesRangesAreaMin);
    const propertiesRangesAreaMax = getMaxValue(variables.propertiesFlatsRangesAreaMax, variables.propertiesHousesRangesAreaMax);
    const propertyPriceMin = getNonZeroMinValue(variables.flatPriceMin, variables.housePriceMin);
    const currency = props.region.country === Country.SPAIN ? "euro" : "zł";

    if (!offerCountForSale || !propertiesCountForSale) {
        return [];
    }

    return [
        {
            headline: `Nieruchomości na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.housesOffersCountForSale + variables.flatsOffersCountForSale,
                    propertiesCountForSale: variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale,
                    offerTypeName: "nowych nieruchomości"
                }),
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Deweloperzy ${variables.regionNameDeclensionWhere} mają w sprzedaży nieruchomości `,
                        `${pluralize([
                            `o powierzchni ${areaFormat(propertiesRangesAreaMin)}`,
                            `o powierzchniach od ${propertiesRangesAreaMin}${
                                propertiesRangesAreaMax !== propertiesRangesAreaMin ? ` do ${areaFormat(propertiesRangesAreaMax)}` : ` m2.`
                            }`,
                            `o powierzchniach od ${propertiesRangesAreaMin}${propertiesRangesAreaMax !== propertiesRangesAreaMin ? ` do ${areaFormat(propertiesRangesAreaMax)}` : " m2."}`
                        ])(variables.housesPropertiesCountForSale + variables.flatsPropertiesCountForSale)}`
                    ]
                }
            ]
        },
        {
            headline: `Ile kosztują nieruchomości ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Najtańsza nieruchomość ${variables.regionNameDeclensionWhere} kosztuje ${priceFormat(propertyPriceMin, {unit: currency})}.`,
                        variables.averagePriceM2 > 0
                            ? ` Średnia cena za m2 nieruchomości ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2, {unit: currency})}.`
                            : ""
                    ]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach nieruchomości ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)}.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na nieruchomości.`
                      },
                {
                    type: ParagraphType.TEXT,
                    text: [
                        `Chcesz mieszkać w domu z własnym ogródkiem? Poznaj `,
                        variables.housesPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href:
                                      props.region.country === Country.SPAIN
                                          ? rpAppLink.offer.listFriendlyAbroad({
                                                friendlySlug: `nowe-domy`,
                                                country: `hiszpania`,
                                                abroadRegionSlug: `${variables.regionSlug}`
                                            })
                                          : rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${variables.regionSlug}`}),
                                  text: `ofertę nowych domów ${variables.regionNameDeclensionWhere}`
                              }
                            : `ofertę nowych domów ${variables.regionNameDeclensionWhere}`,
                        `. Wolisz mieszkanie w mieście? Sprawdź `,
                        variables.flatsPropertiesCountForSale > 0
                            ? {
                                  type: TextType.LINK,
                                  href:
                                      props.region.country === Country.SPAIN
                                          ? rpAppLink.offer.listFriendlyAbroad({
                                                friendlySlug: `nowe-mieszkania`,
                                                country: `hiszpania`,
                                                abroadRegionSlug: `${variables.regionSlug}`
                                            })
                                          : rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${variables.regionSlug}`}),
                                  text: `nowe mieszkania ${variables.regionNameDeclensionWhere}`
                              }
                            : `nowe mieszkania ${variables.regionNameDeclensionWhere}`,
                        ` i znajdź nieruchomość dopasowaną do swoich potrzeb.`
                    ]
                }
            ]
        }
    ];
};
